import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap, Trash2, Cloud
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, SelectItemsForPdfModal, HistorialPedidoCard } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF, generateRetiroStockPDF, agruparPorMaterial } from "./helpers";
import { GenerarPPModal } from "./GenerarPPModal";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters, sortPendingFirst } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";

export function PedidosProveedoresTab({
  supplierOrders, requests, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [viewPP, setViewPP] = useState(null);
  const [pmForOrigen, setPmForOrigen] = useState(null); // PM elegido para abrir el modal de imputación de origen
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pdfSelectPP, setPdfSelectPP] = useState(null); // BLOQUE 18 PARTE A — PP para el que se abrió el modal de selección de ítems del PDF
  const [generatingRSI, setGeneratingRSI] = useState(null); // BLOQUE 24 — pm_id en curso
  const [revertingId, setRevertingId] = useState(null); // BLOQUE 27 PARTE B — PM en curso de "Volver a Aprobaciones"
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");

  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras", "Coordinador de Obra"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);
  // BLOQUE 27 PARTE B — misma allowlist que aprobar + Jefe de Compras (a pedido del cliente)
  const canRevertApproval = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Compras", "Jefe de Compras"]);

  // BLOQUE 27 PARTE B — "Volver a Aprobaciones": revierte un PM aprobado sin PP generado
  const handleRevertApproval = async (pm) => {
    setRevertingId(pm._id);
    try {
      await axios.put(`${API}/purchases/requests/${pm._id}/revert-approval`, {}, { withCredentials: true });
      toast.success(`Pedido ${pm.number} devuelto a Aprobaciones`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al revertir la aprobación"); }
    finally { setRevertingId(null); }
  };

  // BLOQUE 24 — genera (o reimprime, idempotente en backend) el documento de aval
  // de Retiro de Stock Interno para los ítems del PM cubiertos por stock (dentro del PP mixto).
  const handleGenerateRSI = async (pmId) => {
    setGeneratingRSI(pmId);
    try {
      const res = await axios.post(`${API}/purchases/stock-withdrawals`, { pm_id: pmId }, { withCredentials: true });
      generateRetiroStockPDF(res.data);
      toast.success(`Solicitud de retiro de stock interno: ${res.data.number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar la solicitud de retiro");
    } finally {
      setGeneratingRSI(null);
    }
  };

  // BLOQUE 18 PARTE A — al confirmar el modal de selección: genera el PDF solo con los
  // ítems tildados y (si se excluyó algo) registra la trazabilidad en el history del PP.
  // El PP en la base de datos NUNCA se modifica.
  const handleConfirmPdfSelection = async (selectedIndexes, providerName) => {
    const pp = pdfSelectPP;
    if (!pp) return;
    generatePPPDF(pp, selectedIndexes);
    setPdfSelectPP(null);
    const totalItems = (pp.items || []).length;
    const excludedCount = totalItems - selectedIndexes.length;
    if (excludedCount > 0) {
      try {
        await axios.post(`${API}/purchases/supplier-orders/${pp._id}/log-pdf-export`, {
          included_count: selectedIndexes.length,
          excluded_count: excludedCount,
          provider_name: providerName || "",
        }, { withCredentials: true });
      } catch { /* trazabilidad no bloqueante */ }
    }
  };

  const handleDeletePP = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/supplier-orders/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`PP ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar PP"); }
    finally { setDeleting(false); }
  };

  // PMs aprobados que aún no tienen PP generado ni fueron cubiertos 100% por stock interno
  const filterCriteria = { filterCliente, filterObra, filterPresupuesto };
  const filterCatalogos = { listasClientes, listasObras };
  const eligiblePMs = requests.filter(r =>
    r.status === "aprobada" &&
    !r.cubierto_por_stock &&
    !supplierOrders.some(so => so.pm_id === r._id) &&
    matchClienteObraPresupuesto(r, filterCriteria, filterCatalogos)
  );
  const filteredPPsUnsorted = supplierOrders.filter(pp => {
    if (!matchClienteObraPresupuesto(pp, filterCriteria, filterCatalogos)) return false;
    if (filterStatus !== "todos" && pp.status !== filterStatus) return false;
    if (filterUrgency && pp.urgency !== filterUrgency) return false;
    if (search) {
      const s = search.toLowerCase();
      const hit = (pp.number || "").toLowerCase().includes(s) || (pp.pm_number || "").toLowerCase().includes(s) ||
        (pp.lista_obra_nombre || pp.project_name || "").toLowerCase().includes(s) ||
        (pp.presupuesto_codigo || "").toLowerCase().includes(s) ||
        (pp.items || []).some(i => (i.material_name || "").toLowerCase().includes(s));
      if (!hit) return false;
    }
    return true;
  });
  const statusCounts = supplierOrders.filter(pp => matchClienteObraPresupuesto(pp, filterCriteria, filterCatalogos)).reduce((acc, pp) => { acc[pp.status] = (acc[pp.status] || 0) + 1; return acc; }, {});
  // BLOQUE 50 PARTE B — PP pendientes/en análisis arriba, cerrados abajo; más nuevo primero.
  const filteredPPs = sortPendingFirst(filteredPPsUnsorted, pp => pp.status !== "cerrado");

  const ppStatusBadge = (status) => {
    const map = { pendiente: "pendiente", en_analisis: "analisis_presupuesto", cerrado: "presupuesto_aprobado" };
    return map[status] || "pendiente";
  };

  return (
    <div className="space-y-6">

      {/* Filtro Cliente -> Obra -> Presupuesto (BLOQUE 27) + Buscar/Estado/Urgencia (BLOQUE 50) */}
      <div className="flex items-center gap-2 flex-wrap">
        <FiltroClienteObraPresupuesto
          listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
          filterCliente={filterCliente} setFilterCliente={setFilterCliente}
          filterObra={filterObra} setFilterObra={setFilterObra}
          filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
          testIdPrefix="ped-proveedores"
        />
        <BusquedaEstadoUrgenciaFilters
          search={search} setSearch={setSearch}
          searchPlaceholder="Buscar por N° PP, PM, obra o material..."
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          statusOptions={[
            { value: "todos", label: `Todos (${supplierOrders.filter(pp => matchClienteObraPresupuesto(pp, filterCriteria, filterCatalogos)).length})` },
            { value: "pendiente", label: `Pendiente (${statusCounts.pendiente || 0})` },
            { value: "en_analisis", label: `En Análisis (${statusCounts.en_analisis || 0})` },
            { value: "cerrado", label: `Cerrado (${statusCounts.cerrado || 0})` },
          ]}
          filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
          testIdPrefix="ped-proveedores"
        />
        {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
          <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
            data-testid="ped-proveedores-clear-filter">
            <X size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* Sección: PMs Aprobados — Pendiente de PP */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-bold text-gray-900">Pedidos Aprobados — Pendiente de generar PP</h3>
          {eligiblePMs.length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full border border-amber-200">{eligiblePMs.length}</span>
          )}
        </div>

        {loading ? (
          <div className="space-y-2">{[1,2].map(i => <Skeleton key={i} className="h-16" />)}</div>
        ) : eligiblePMs.length === 0 ? (
          <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-6 text-center">
            <CheckCircle2 size={28} className="mx-auto text-emerald-400 mb-2" />
            <p className="text-sm font-medium text-emerald-700">Todos los pedidos aprobados ya tienen PP generado</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-amber-50 border-b border-amber-100">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-amber-800 uppercase">Pedido</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-amber-800 uppercase hidden sm:table-cell">Proyecto</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-amber-800 uppercase">Ítems</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-amber-800 uppercase hidden md:table-cell">Fecha objetivo</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-50">
                {eligiblePMs.map(r => (
                  <tr key={r._id} className="hover:bg-amber-50/50 transition-colors" data-testid="eligible-pm-row">
                    <td className="px-4 py-3">
                      <p className="font-mono font-bold text-gray-900">{r.number}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Por: {r.requested_by_name}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700 hidden sm:table-cell">{r.project_name}</td>
                    <td className="px-4 py-3">
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{r.items?.length || 0} ítems</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">
                      {r.target_date ? <span className="flex items-center gap-1"><Calendar size={10} />{formatDate(r.target_date)}</span> : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setPmForOrigen(r)}
                          className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-60 whitespace-nowrap"
                          data-testid="generate-pp-inline-btn">
                          <Package size={12} />
                          Generar PP
                        </button>
                        {canRevertApproval && (
                          <button
                            onClick={() => handleRevertApproval(r)}
                            disabled={revertingId === r._id}
                            title="Devolver este pedido al área de Aprobaciones"
                            className="flex items-center gap-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-60 whitespace-nowrap"
                            data-testid={`revert-approval-btn-${r._id}`}>
                            {revertingId === r._id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                            Volver a Aprobaciones
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sección: PPs Generados */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">Pedidos de Presupuesto (PP) Generados</h3>
          <span className="text-xs text-gray-500">{filteredPPs.length} de {supplierOrders.length} en total</span>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}</div>
        ) : filteredPPs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200/60 p-10 text-center">
            <Package size={36} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm font-medium">No hay PPs generados aún</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPPs.map(pp => (
              <div key={pp._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm hover:shadow-md transition-all" data-testid="pp-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-900 text-sm font-mono">{pp.number}</p>
                      <StatusBadge status={ppStatusBadge(pp.status)} />
                      {pp.obra_rapida && (
                        <span className="inline-flex items-center gap-1.5 bg-sky-100 text-sky-800 border border-sky-300 rounded-full px-2.5 py-0.5 text-[10px] font-bold" data-testid={`pp-obra-rapida-badge-${pp._id}`}>
                          <Cloud size={11} className="text-sky-600" /> Pedido para obra rápida / A vincular con presupuesto
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 font-medium mt-1" data-testid="pp-card-obra">
                      {pp.lista_obra_nombre || pp.project_name}
                      {pp.lista_cliente_nombre ? <span className="text-gray-400 font-normal"> · {pp.lista_cliente_nombre}</span> : null}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      PM: {pp.pm_number}{pp.presupuesto_codigo ? ` · PPTO: ${pp.presupuesto_codigo}` : ""} · {agruparPorMaterial(pp.items).length} ítems · {pp.created_by_name} · {formatDate(pp.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setViewPP(pp)} title="Ver PP"
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-[#2E6DA4] transition-colors"
                      data-testid="ver-pp-btn">
                      <Eye size={15} />
                    </button>
                    <button onClick={() => setPdfSelectPP(pp)} title="Descargar PDF"
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="pdf-pp-btn">
                      <Download size={15} />
                    </button>
                    {canDelete && (
                      <button onClick={() => setDeleteTarget(pp)} title="Eliminar PP"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                        data-testid={`delete-pp-btn-${pp._id}`}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal VER PP */}
      {viewPP && (
        <Modal open={!!viewPP} onClose={() => setViewPP(null)} title={`PP: ${viewPP.number}`} extraWide>
          <div className="space-y-5">
            <div className="bg-[#2E6DA4] rounded-xl p-5 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium opacity-70 uppercase tracking-wider">CEN INGENIERIA E.A.S.</p>
                  <p className="text-xs opacity-60 mt-0.5">Instalaciones Eléctricas — Asunción, Paraguay</p>
                </div>
                <div className="text-right">
                  <p className="text-xs opacity-70 uppercase">Pedido de Presupuesto</p>
                  <p className="font-mono font-bold text-lg mt-0.5">{viewPP.number}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: "Obra", value: viewPP.lista_obra_nombre || viewPP.project_name },
                { label: "Cliente", value: viewPP.lista_cliente_nombre },
                { label: "Presupuesto", value: viewPP.presupuesto_codigo },
                { label: "PM Origen", value: viewPP.pm_number },
                { label: "Generado por", value: viewPP.created_by_name },
                { label: "Fecha", value: formatDateTime(viewPP.created_at) },
                { label: "Fecha Objetivo", value: viewPP.target_date ? formatDate(viewPP.target_date) : "—" },
                { label: "Total ítems", value: `${agruparPorMaterial(viewPP.items).length} materiales` },
                { label: "Estado", value: viewPP.status },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1">{value || "—"}</p>
                </div>
              ))}
            </div>
            {/* Fix O (2026-08-31): historial del pedido de origen en cada paso del flujo. */}
            <HistorialPedidoCard pmId={viewPP.pm_origen_id || viewPP.pm_id} />
            <div>
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Materiales Solicitados (para cotización)</h4>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#2E6DA4] text-white">
                      {["#", "Descripción", "UM", "Cant.", "Marca Recomendada"].map(h => (
                        <th key={h} className="text-left px-3 py-2.5 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agruparPorMaterial(viewPP.items).map((item, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-blue-50/40"}>
                        <td className="px-3 py-2.5 font-bold text-[#2E6DA4]">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium text-gray-800">
                          {item.material_name}
                          {item.__lineas > 1 && <span className="block text-[10px] font-normal text-gray-400" data-testid={`pp-item-lineas-${i}`}>Total de {item.__lineas} ítems del presupuesto</span>}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500">{item.unit}</td>
                        <td className="px-3 py-2.5 font-bold text-gray-900">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-gray-500 italic">{item.marca_recomendada || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PROMPT BLOQUE 4: sección informativa de ítems cubiertos por stock interno (no cotizados) */}
            {viewPP.stock_coverage_summary?.count > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pp-stock-coverage-section">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                    {viewPP.stock_coverage_summary.count} ítem{viewPP.stock_coverage_summary.count !== 1 ? "s" : ""} cubierto{viewPP.stock_coverage_summary.count !== 1 ? "s" : ""} por stock interno (no incluido{viewPP.stock_coverage_summary.count !== 1 ? "s" : ""} en esta cotización)
                  </p>
                  <button
                    type="button"
                    onClick={() => handleGenerateRSI(viewPP.pm_id)}
                    disabled={generatingRSI === viewPP.pm_id}
                    className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-60 whitespace-nowrap"
                    data-testid="generate-rsi-pp-btn"
                    title="Documento de aval imprimible para solicitar el retiro de stock interno"
                  >
                    {generatingRSI === viewPP.pm_id ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                    Solicitud de retiro de stock
                  </button>
                </div>
                <div className="space-y-1">
                  {(viewPP.stock_coverage_summary.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-1.5 border border-amber-100">
                      <span className="font-medium text-gray-700">{it.material_name} <span className="text-gray-400">({it.quantity} {it.unit})</span></span>
                      <span className="text-amber-700 font-semibold">{it.origen === "stock_herramientas" ? "Herramienta" : "Material"}: {it.origen_material_code} · {it.origen_material_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => setPdfSelectPP(viewPP)}
                className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                data-testid="pdf-pp-modal-btn">
                <Download size={14} /> Descargar PDF
              </button>
              <button onClick={() => setViewPP(null)}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Generar PP — Imputar Origen (PROMPT BLOQUE 4) */}
      <GenerarPPModal
        pm={pmForOrigen}
        open={!!pmForOrigen}
        onClose={() => setPmForOrigen(null)}
        onGenerated={() => { setPmForOrigen(null); onRefresh(); }}
      />

      {/* Modal BLOQUE 18 PARTE A — seleccionar ítems para el PDF del PP */}
      <SelectItemsForPdfModal
        open={!!pdfSelectPP}
        onClose={() => setPdfSelectPP(null)}
        items={pdfSelectPP?.items || []}
        itemLabel={(it) => `${it.material_name || "—"} (${it.quantity ?? "—"} ${it.unit || ""})`}
        onConfirm={handleConfirmPdfSelection}
      />

      {/* Modal confirmar eliminación de PP (BLOQUE 6 PARTE C.2) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Pedido a Proveedores">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el PP <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeletePP} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-pp-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

